import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import type { AlertItem, AlertSeverity } from '@/types/dashboards'
import FigureValue from './FigureValue'
import { useDrillDown } from './useDrillDown'

const SEVERITY_DOT: Record<AlertSeverity, string> = {
  critical: 'bg-red-500',
  high: 'bg-amber-400',
  medium: 'bg-amber-500/60',
  low: 'bg-stone-600',
}

interface AlertRailProps {
  alerts: AlertItem[]
  className?: string
}

/**
 * Critical Alerts rail. Every entry is a hard predicate from the server (a
 * potential sanctions match, an overdue finding count) — not a score — so the
 * rail is empty when nothing qualifies rather than padded with near-misses.
 */
export default function AlertRail({ alerts, className }: AlertRailProps) {
  const navigate = useNavigate()
  const { open } = useDrillDown()

  if (alerts.length === 0) {
    return (
      <p className={clsx('text-[11px] text-stone-500', className)}>
        No alert thresholds are breached right now.
      </p>
    )
  }

  return (
    <ul className={clsx('space-y-1.5', className)}>
      {alerts.map((alert) => {
        const clickable = Boolean(alert.drill || alert.href)
        return (
          <li key={alert.id}>
            <div
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={
                clickable
                  ? () => (alert.drill ? open(alert.drill) : navigate(alert.href as string))
                  : undefined
              }
              onKeyDown={
                clickable
                  ? (event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      if (alert.drill) open(alert.drill)
                      else navigate(alert.href as string)
                    }
                  : undefined
              }
              className={clsx(
                'surface-panel flex items-start gap-2 rounded-md px-2.5 py-2 transition-colors',
                clickable && 'cursor-pointer hover:border-amber-400/30 hover:bg-amber-400/[0.04]',
              )}
            >
              <span
                className={clsx(
                  'status-dot mt-1 flex-shrink-0',
                  SEVERITY_DOT[alert.severity],
                )}
                aria-label={alert.severity}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium leading-snug text-stone-200">
                  {alert.title}
                </div>
                {alert.detail && (
                  <div className="mt-0.5 text-[10px] leading-relaxed text-stone-500">
                    {alert.detail}
                  </div>
                )}
              </div>
              {alert.figure && (
                <span className="flex-shrink-0 font-mono text-[11px] tabular-nums text-amber-200">
                  <FigureValue figure={alert.figure} align="right" />
                </span>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
